/**
 * Map Management API client.
 */
import { getIdToken } from '../contexts/AuthContext';
import { buildApiUrl, getAuthHeaders } from '../config/api';

async function authedFetch(endpoint, options = {}) {
  const idToken = await getIdToken();
  if (!idToken) throw new Error('Not authenticated. Please log in.');

  const response = await fetch(buildApiUrl(endpoint), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(idToken),
      ...(options.headers || {}),
    },
  });

  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body.message || body.error || `Request failed: ${response.statusText}`);
    error.payload = body;
    throw error;
  }
  return body;
}

/** Pull a Mapbox dataset and sanitize it against a ski area. */
export const startImport = (skiAreaId, mapboxUrl) =>
  authedFetch('/map-management/imports', {
    method: 'POST',
    body: JSON.stringify({ skiAreaId, mapboxUrl }),
  });

/** Geometry for the review map: current, incoming, and both boundaries. */
export const fetchPreview = (runId) =>
  authedFetch(`/map-management/imports/${encodeURIComponent(runId)}/preview`);

/**
 * Publish an import. Destructive: replaces the ski area's runs, lifts and POIs
 * and moves the live pointer.
 *
 * Rejects with `error.payload.error === 'stale_base'` when the resort was
 * published again after this import was sanitized; re-call with
 * acknowledgeStaleBase to publish over it anyway.
 */
export const integrateImport = (runId, message, acknowledgeStaleBase = false) =>
  authedFetch(`/map-management/imports/${encodeURIComponent(runId)}/integrate`, {
    method: 'POST',
    body: JSON.stringify({ message, acknowledgeStaleBase }),
  });

/** Published versions, newest first, plus the pointer. */
export const fetchVersions = async (skiAreaId) => {
  const qs = skiAreaId ? `?skiAreaId=${encodeURIComponent(skiAreaId)}` : '';
  return authedFetch(`/map-management/versions${qs}`);
};

/** Move the live pointer to an existing version. */
export const promoteVersion = (versionId, reason = 'rollback') =>
  authedFetch(`/map-management/versions/${encodeURIComponent(versionId)}/promote`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });

/** Tag a version with the app/tiles versions it's compatible with. */
export const updateVersionTags = (versionId, tags) =>
  authedFetch(`/map-management/versions/${encodeURIComponent(versionId)}/tags`, {
    method: 'POST',
    body: JSON.stringify(tags),
  });

/** Who currently holds the integration lock, or null. */
export const fetchLock = async () => (await authedFetch('/map-management/lock')).lock;

/**
 * One ski area as the serving version holds it: boundary, runs, lifts and POIs
 * with vertex heights, decoded contour lines, and terrain sampling metadata.
 */
export const fetchSkiAreaMap = (skiAreaId) =>
  authedFetch(`/map-management/ski-areas/${encodeURIComponent(skiAreaId)}/map`);
